import React, { useState, useRef, useEffect } from 'react';
import { ReactCrop, type Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RotateCcw, RotateCw, Check, X, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface ImageCropDialogProps {
  imageUrl: string;
  open: boolean;
  onClose: () => void;
  onSave: (croppedImageUrl: string) => Promise<void>;
  title?: string;
}

const ImageCropDialog: React.FC<ImageCropDialogProps> = ({
  imageUrl,
  open,
  onClose,
  onSave,
  title
}) => {
  const { t } = useTranslation(['shared']);
  
  // Use translated title if none provided
  const dialogTitle = title || t('imageCrop.title');
  
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 100,
    height: 100,
    x: 0,
    y: 0
  });
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const rotationInterval = useRef<NodeJS.Timeout>();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      console.log('Dialog opened, resetting state');
      setRotation(0);
      setScale(1);
      setImageLoaded(false);
      setCrop({
        unit: '%',
        width: 100,
        height: 100,
        x: 0,
        y: 0
      });
    }
  }, [open]);

  // Function to handle single rotation step
  const handleSingleRotation = (direction: 'left' | 'right') => {
    setRotation(prev => Math.round((direction === 'left' ? prev - 0.1 : prev + 0.1) * 10) / 10);
  };

  const startRotation = (direction: 'left' | 'right') => {
    // Clear any existing interval
    if (rotationInterval.current) {
      clearInterval(rotationInterval.current);
    }
  
    // Initial rotation
    handleSingleRotation(direction);
  
    // Initial interval (slow start)
    let currentInterval = 300;
    const minInterval = 20;
  
    const startTime = Date.now();
  
    const updateRotation = () => {
      handleSingleRotation(direction);
  
      const elapsedTime = Date.now() - startTime;
      const newInterval = Math.max(
        minInterval,
        300 - Math.floor(elapsedTime / 200) * 40
      );
  
      if (newInterval !== currentInterval) {
        currentInterval = newInterval;
        clearInterval(rotationInterval.current!);
        rotationInterval.current = setInterval(updateRotation, currentInterval);
      }
    };
  
    // Start with slow interval
    rotationInterval.current = setInterval(updateRotation, currentInterval);
  };
  

  // Function to handle pointer down (works for both mouse and touch)
  const handlePointerDown = (direction: 'left' | 'right') => {
    startRotation(direction);
  };

  // Function to handle pointer up (works for both mouse and touch)
  const handlePointerUp = () => {
    stopRotation();
  };

  const stopRotation = () => {
    console.log('Stopping rotation');
    if (rotationInterval.current) {
      clearInterval(rotationInterval.current);
    }
  };

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      console.log('Cleaning up rotation interval');
      if (rotationInterval.current) {
        clearInterval(rotationInterval.current);
      }
    };
  }, []);

  // Handle image load to get natural dimensions
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    // console.log('Image loaded, getting dimensions');
    const img = e.currentTarget;
    setImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight
    });
    setImageLoaded(true);
    // console.log('Image dimensions set:', { width: img.naturalWidth, height: img.naturalHeight });
  };

  const handleZoom = (direction: 'in' | 'out') => {
    setScale(prevScale => {
      const step = 0.1;
      const newScale = direction === 'in' ? prevScale + step : prevScale - step;
      // Limit scale between 0.1 and 3
      return Math.max(0.1, Math.min(3, newScale));
    });
  };

  const handleSave = async () => {
    if (!imgRef.current || !crop) {
      return;
    }

    try {
      setLoading(true);
      const image = imgRef.current;

      // Calculate how the image is drawn in the display (same as object-contain)
      const imageAspectRatio = image.naturalWidth / image.naturalHeight;
      const displayAspectRatio = image.width / image.height;

      let drawWidth, drawHeight;
      if (imageAspectRatio > displayAspectRatio) {
        // Image is wider than display - fit to height
        drawHeight = image.height;
        drawWidth = drawHeight * imageAspectRatio;
      } else {
        // Image is taller than display - fit to width
        drawWidth = image.width;
        drawHeight = drawWidth / imageAspectRatio;
      }

      // Calculate the required canvas size to accommodate the entire transformed image
      // We need enough space for the rotated and scaled image
      const maxDimension = Math.max(drawWidth * scale, drawHeight * scale);
      const canvasSize = Math.ceil(maxDimension * 1.5); // Add 50% padding for rotation

      // Create a canvas that's large enough to hold the entire transformed image
      const displayCanvas = document.createElement("canvas");
      const displayCtx = displayCanvas.getContext("2d");
      if (!displayCtx) throw new Error("Could not get display canvas context");

      // Set canvas size based on the transformed image size
      displayCanvas.width = canvasSize;
      displayCanvas.height = canvasSize;

      // Apply the exact same transformations as the displayed image
      displayCtx.save();
      displayCtx.translate(canvasSize / 2, canvasSize / 2);
      displayCtx.rotate((rotation * Math.PI) / 180);
      displayCtx.scale(scale, scale);

      // Draw the image centered (same as object-contain behavior)
      displayCtx.drawImage(
        image,
        -drawWidth / 2,
        -drawHeight / 2,
        drawWidth,
        drawHeight
      );

      displayCtx.restore();

      // Calculate the offset of the display area within the large canvas
      const displayOffsetX = (canvasSize - image.width) / 2;
      const displayOffsetY = (canvasSize - image.height) / 2;

      console.log('🔍 [ImageCropDialog] Canvas created:', {
        canvasSize,
        displayDimensions: { width: image.width, height: image.height },
        drawDimensions: { width: drawWidth, height: drawHeight },
        displayOffset: { x: displayOffsetX, y: displayOffsetY },
        transformations: { rotation, scale }
      });

      // Convert crop coordinates to pixel coordinates
      const cropPx = {
        x: crop.unit === "%" ? (crop.x / 100) * image.width : crop.x,
        y: crop.unit === "%" ? (crop.y / 100) * image.height : crop.y,
        width: crop.unit === "%" ? (crop.width / 100) * image.width : crop.width,
        height: crop.unit === "%" ? (crop.height / 100) * image.height : crop.height
      };

      console.log('🔍 [ImageCropDialog] Crop coordinates:', {
        originalCrop: crop,
        cropPx,
        finalCropArea: {
          x: cropPx.x + displayOffsetX,
          y: cropPx.y + displayOffsetY,
          width: cropPx.width,
          height: cropPx.height
        }
      });

      // Create final output canvas
      const outputCanvas = document.createElement('canvas');
      const outputCtx = outputCanvas.getContext('2d');

      if (!outputCtx) {
        throw new Error('No 2d context for output');
      }

      outputCanvas.width = Math.abs(cropPx.width);
      outputCanvas.height = Math.abs(cropPx.height);

      outputCtx.imageSmoothingEnabled = true;
      outputCtx.imageSmoothingQuality = 'high';

      // Extract the cropped area
      outputCtx.drawImage(
        displayCanvas,
        cropPx.x + displayOffsetX,
        cropPx.y + displayOffsetY,
        cropPx.width,
        cropPx.height,
        0,
        0,
        cropPx.width,
        cropPx.height
      );

      // Convert to blob with high quality
      const blob = await new Promise<Blob>((resolve, reject) => {
        outputCanvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          },
          'image/jpeg',
          0.95
        );
      });

      const croppedImageUrl = URL.createObjectURL(blob);
      await onSave(croppedImageUrl);
      
      onClose();
    } catch (error) {
      console.error('Error in handleSave:', error);
      toast.error(t('imageCrop.failedToSave'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(isOpen) => {
        if (!isOpen && !loading) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-[95vw] sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[70vw] h-[90vh] overflow-y-auto p-4 sm:p-6">
                 <DialogHeader>
           <DialogTitle className="text-lg sm:text-xl"> <span> {dialogTitle} </span> </DialogTitle>
         </DialogHeader>

        <div className="space-y-4 sm:space-y-6">
          <div className="relative flex justify-center items-center bg-muted rounded-lg overflow-hidden">
            {imageLoaded ? (
              <ReactCrop
                crop={crop}
                onChange={(c) => {
                  setCrop(c);
                }}
                className="max-h-[50vh] sm:max-h-[60vh] w-auto"
              >
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="Crop preview"
                  crossOrigin="anonymous"
                  onLoad={handleImageLoad}
                  style={{ 
                    maxHeight: '50vh',
                    width: 'auto',
                    transform: `rotate(${rotation}deg) scale(${scale})`,
                    transition: 'transform 0.1s ease'
                  }}
                  className="object-contain"
                />
              </ReactCrop>
            ) : (
              <div className="w-full h-[50vh] sm:h-[60vh] flex items-center justify-center">
                <img
                  src={imageUrl}
                  alt="Loading preview"
                  crossOrigin="anonymous"
                  onLoad={handleImageLoad}
                  className="hidden"
                />
                <span>{t('imageCrop.loadingImage')}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-2">
            <div className="flex gap-2 items-center w-full sm:w-auto">
              {/* Rotation Controls */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 sm:h-8 sm:w-8 touch-none" // Added touch-none to prevent default touch behavior
                  onPointerDown={() => handlePointerDown('left')}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                  disabled={loading}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 sm:h-8 sm:w-8 touch-none" // Added touch-none to prevent default touch behavior
                  onPointerDown={() => handlePointerDown('right')}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                  disabled={loading}
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground ml-2">
                  {rotation.toFixed(1)}°
                </span>
              </div>

              <div className="border-l mx-2 h-6" />

              {/* Zoom Controls */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 sm:h-8 sm:w-8" // Larger touch target on mobile
                  onClick={() => handleZoom('out')}
                  disabled={loading || scale <= 0.1}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 sm:h-8 sm:w-8" // Larger touch target on mobile
                  onClick={() => handleZoom('in')}
                  disabled={loading || scale >= 3}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground ml-2">
                  {Math.round(scale * 100)}%
                </span>
              </div>
            </div>

            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="flex-1 sm:flex-initial"
              >
                <X className="h-4 w-4 mr-2" />
                {t('imageCrop.cancel')}
              </Button>
              <Button
                variant="default"
                onClick={handleSave}
                disabled={loading || !crop}
                className="flex-1 sm:flex-initial"
              >
                <Check className="h-4 w-4 mr-2" />
                {t('imageCrop.save')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropDialog; 